import React, { ReactElement, useMemo } from 'react';
import { FormattedMessage, formatTranslation } from 'intl';
import { WorkflowTranslationKeys } from 'intl/keys';
import { useIntl } from 'react-intl';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { Edit } from '@mui/icons-material';
import { FieldBox } from 'components/fieldBox';
import { ButtonIcon } from 'components/general';
import { DialogButtons } from 'components/general/ButtonsPanel/ButtonsPanel';
import { MemoProcessIcon } from 'components/common/ProcessIcon';
import type { Color } from 'shared/utils/colors';
import type { IssueWorkflowOutDto } from 'shared/domain/workflow/types';
import { getWorkflowStagesApprovalPresence } from 'shared/domain/workflow/types';
import type { WorkflowConfigurationPresentationalProps } from './types';
import {
  rootSx,
  headerStackSx,
  loaderBoxSx,
  emptyPaperSx,
  listStackSx,
  listItemContentSx,
  chipSx,
} from './styles';

type WorkflowListItemProps = {
  workflow: IssueWorkflowOutDto;
  processes: { color: string; code: string }[] | undefined;
  intl: ReturnType<typeof useIntl>;
  createLoading: boolean;
  onNavigateToWorkflowCreator: (w: IssueWorkflowOutDto) => void;
};

function WorkflowListItem({
  workflow,
  processes,
  intl,
  createLoading,
  onNavigateToWorkflowCreator,
}: WorkflowListItemProps): ReactElement {
  const activeStages = useMemo(
    () => (workflow.stages ?? []).filter((s) => !s.deleted),
    [workflow.stages]
  );

  const approvalSummary = useMemo(
    () => getWorkflowStagesApprovalPresence(activeStages),
    [activeStages]
  );

  const hasAssignedApprovers = useMemo(
    () => activeStages.some((stage) => (stage.approvers?.length ?? 0) > 0),
    [activeStages]
  );

  const editDisabled = createLoading;

  return (
    <FieldBox dataQa={`workflow-list-item-${workflow._id}`}>
      <Box sx={listItemContentSx}>
        <Typography component='span'>{workflow.name}</Typography>
        <Stack
          direction='row'
          spacing={0.5}
          flexWrap='wrap'
          useFlexGap
          alignItems='center'
        >
          {processes?.map((process, index) => (
            <MemoProcessIcon
              key={`${process.code}-${index}`}
              color={process.color as Color}
              code={process.code}
            />
          ))}
          {workflow.isLocked ? (
            <Chip
              size='small'
              color='default'
              label={formatTranslation(intl, WorkflowTranslationKeys.configurationInUseChip)}
              sx={chipSx}
            />
          ) : null}
          <Chip
            size='small'
            variant='outlined'
            label={formatTranslation(intl, WorkflowTranslationKeys.configurationStagesChip , { count: activeStages.length }
            )}
            sx={chipSx}
          />
          {approvalSummary.hasParallel ? (
            <Chip
              size='small'
              variant='outlined'
              label={
                <FormattedMessage
                  id={WorkflowTranslationKeys.configurationParallelChip}
                />
              }
              sx={chipSx}
            />
          ) : null}
          {approvalSummary.hasSequential ? (
            <Chip
              size='small'
              variant='outlined'
              label={
                <FormattedMessage
                  id={WorkflowTranslationKeys.configurationSequentialChip}
                />
              }
              sx={chipSx}
            />
          ) : null}
        </Stack>
      </Box>
      <ButtonIcon
        aria-label={formatTranslation(
          intl,
          WorkflowTranslationKeys.configurationEditAria
        )}
        title={formatTranslation(
          intl,
          WorkflowTranslationKeys.configurationEditAria
        )}
        onClick={() => onNavigateToWorkflowCreator(workflow)}
        disabled={editDisabled}
        data-qa={`workflow-edit-${workflow._id}`}
      >
        <Edit />
      </ButtonIcon>
    </FieldBox>
  );
}

export function WorkflowConfigurationPresentational(
  props: WorkflowConfigurationPresentationalProps
): ReactElement {
  const intl = useIntl();
  const {
    projectId,
    sortedWorkflows,
    workflowToProcess,
    loadingList,
    createOpen,
    createName,
    createWorkflow,
    createLoading,
    onOpenCreate,
    onCloseCreate,
    onChangeCreateName,
    onSubmitCreate,
    onNavigateToWorkflowCreator,
  } = props;

  const showInitialLoader = loadingList && sortedWorkflows.length === 0;

  return (
    <Box sx={rootSx}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        alignItems={{ xs: 'stretch', sm: 'center' }}
        justifyContent='space-between'
        sx={headerStackSx}
      >
        <Typography variant='h5' component='h1'>
          <FormattedMessage
            id={WorkflowTranslationKeys.configurationPageTitle}
          />
        </Typography>
        <Button
          variant='contained'
          color='primary'
          onClick={onOpenCreate}
          disabled={!projectId || createWorkflow.loading}
          data-qa='workflow-create-open'
        >
          <FormattedMessage
            id={WorkflowTranslationKeys.configurationCreateButton}
          />
        </Button>
      </Stack>

      {showInitialLoader ? (
        <Box sx={loaderBoxSx}>
          <CircularProgress />
        </Box>
      ) : sortedWorkflows.length === 0 ? (
        <Paper variant='outlined' sx={emptyPaperSx}>
          <Typography color='text.secondary'>
            <FormattedMessage
              id={WorkflowTranslationKeys.configurationEmptyState}
            />
          </Typography>
        </Paper>
      ) : (
        <Stack spacing={1} sx={listStackSx}>
          {sortedWorkflows.map((workflow) => (
            <WorkflowListItem
              key={workflow._id}
              workflow={workflow}
              processes={workflowToProcess[workflow._id]}
              intl={intl}
              createLoading={createLoading}
              onNavigateToWorkflowCreator={onNavigateToWorkflowCreator}
            />
          ))}
        </Stack>
      )}

      <Dialog
        fullWidth
        maxWidth='sm'
        open={createOpen}
        onClose={onCloseCreate}
      >
        <DialogTitle>
          <FormattedMessage
            id={WorkflowTranslationKeys.configurationDialogCreateTitle}
          />
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ overflow: 'auto', maxHeight: '70vh' }}>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField
              autoFocus
              fullWidth
              label={formatTranslation(intl, WorkflowTranslationKeys.configurationNameLabel,)}
              value={createName}
              onChange={(e) => onChangeCreateName(e.target.value)}
              data-qa='workflow-create-name'
            />
          </Stack>
        </DialogContent>
        <Divider />
        <DialogActions>
          <DialogButtons
            onCancel={onCloseCreate}
            onSubmit={onSubmitCreate}
            isPosting={createWorkflow.loading || createLoading}
            submitDisabled={!createName.trim()}
          />
        </DialogActions>
      </Dialog>
    </Box>
  );
}
